import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import TablePagination from "./TablePagination";
import {
  getProfileCorrections,
  getProfileCorrectionById,
  updateProfileCorrectionStatus,
} from "../Apis/ProfileCorrectionAPIs";
import { getProducts } from "../Apis/ProductAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { getCategories } from "../Apis/CategoryAPIs";

// Helper to extract only the year from date/year strings
const getYearOnly = (val) => {
  if (!val) return "";
  const cleaned = String(val).trim();
  if (cleaned.length === 4 && /^\d{4}$/.test(cleaned)) return cleaned;
  const d = new Date(val);
  return !isNaN(d.getFullYear()) ? String(d.getFullYear()) : cleaned;
};

// Helper to map numeric ratings to readable text labels
const mapRatingValue = (val) => {
  if (!val) return "";
  const mapping = { "1": "VIP", "2": "ENQUIRY", "3": "OTHER EXHIBITOR", "4": "OWN EXHIBITOR" };
  return mapping[String(val)] || val;
};

// ── Compute changed fields between oldValue and new values on record ─────────
const FIELD_LABELS = {
  companyName: "Company Name",
  keyPerson: "Key Person",
  email: "Email",
  mobile1: "Mobile 1",
  mobile2: "Mobile 2",
  mobile3: "Mobile 3",
  landline: "Landline",
  whatsappNumber: "WhatsApp Number",
  websiteLink: "Website",
  city: "City",
  state: "State",
  pincode: "Pincode",
  ratings: "Ratings",
  address: "Address",
  customerSince: "Customer Since",
  segment: "Segment",
  categoryId: "Exhibition Type",
};

// For list-level diff (scalar fields only, used in table rows)
const getChangedFields = (record, allCategories = []) => {
  if (!record?.oldValue) return [];
  const changes = [];

  const getCategoryName = (val) => {
    if (!val) return "";
    const id = typeof val === "object" ? val._id || val.id : val;
    if (!id) return "";
    const found = (allCategories || []).find((c) => (c._id || c.id) === id);
    return found ? found.name : id;
  };
  
  Object.keys(FIELD_LABELS).forEach((field) => {
    let oldVal = record.oldValue[field] ?? "";
    let newVal = record[field] ?? "";
    
    // Address comparison fallback for backward compatibility
    if (field === "address") {
      oldVal = record.oldValue.address || record.oldValue.street || "";
      newVal = record.address || record.street || "";
    }

    // Customer Since comparison (only compare and display year)
    if (field === "customerSince") {
      oldVal = getYearOnly(oldVal);
      newVal = getYearOnly(newVal);
    }

    if (field === "categoryId") {
      oldVal = getCategoryName(oldVal);
      newVal = getCategoryName(newVal);
    }
    
    if (String(oldVal).trim() !== String(newVal).trim()) {
      const displayOldVal = field === "ratings" ? mapRatingValue(oldVal) : oldVal;
      const displayNewVal = field === "ratings" ? mapRatingValue(newVal) : newVal;
      changes.push({
        field,
        label: FIELD_LABELS[field],
        oldVal: String(displayOldVal || "—"),
        newVal: String(displayNewVal || "—"),
      });
    }
  });

  // For backward compatibility: check legacy street, area, city if address was not changed/present
  const addressChange = changes.find(c => c.field === "address");
  if (!addressChange) {
    ["street", "area", "city"].forEach((legacyField) => {
      const oldVal = record.oldValue[legacyField] ?? "";
      const newVal = record[legacyField] ?? "";
      if (newVal && String(oldVal).trim() !== String(newVal).trim()) {
        const legacyLabels = { street: "Street", area: "Area", city: "City" };
        changes.push({
          field: legacyField,
          label: legacyLabels[legacyField],
          oldVal: String(oldVal || "—"),
          newVal: String(newVal || "—"),
        });
      }
    });
  }

  return changes;
};

// For detail modal diff — includes products & interestedLocations
const getDetailChangedFields = (request, client, allProducts, allLocations, allCategories) => {
  const scalar = getChangedFields(request, allCategories);

  // Products diff
  const oldProducts = request.oldValue?.products || [];
  const newProducts = request.products || [];

  const getProductName = (p) => {
    if (!p) return "";
    if (typeof p === "object") {
      if (p.productName) return p.productName;
      if (p.name) return p.name;
    }
    const id = typeof p === "object" ? p._id : p;
    if (!id) return "";

    // Search in allProducts master list
    const foundMaster = (allProducts || []).find((item) => (item._id || item.id || item) === id);
    if (foundMaster && typeof foundMaster === "object") {
      return foundMaster.productName || foundMaster.name || id;
    }

    // Search in newProducts
    const foundInNew = newProducts.find((item) => typeof item === "object" && (item._id || item) === id);
    if (foundInNew && foundInNew.productName) {
      return foundInNew.productName;
    }

    // Search in client.products
    const foundInClient = (client?.products || []).find((item) => typeof item === "object" && (item._id || item) === id);
    if (foundInClient && foundInClient.productName) {
      return foundInClient.productName;
    }

    return id;
  };

  const oldProductNames = oldProducts.map(getProductName);
  const newProductNames = newProducts.map((p) => (typeof p === "object" ? p.productName || p._id || p : p));

  const oldProductIds = oldProducts.map((p) => (typeof p === "object" ? p._id : p));
  const newProductIds = newProducts.map((p) => (typeof p === "object" ? p._id : p));

  const productsDiff =
    oldProductIds.length !== newProductIds.length ||
    !oldProductIds.every((id) => newProductIds.some((newId) => newId === id));

  if (productsDiff) {
    scalar.push({
      field: "products",
      label: "Products",
      oldVal: oldProductNames.length
        ? oldProductNames.join(", ")
        : "None",
      newVal: newProductNames.length
        ? newProductNames.join(", ")
        : "None",
      isArray: true,
      oldList: oldProductNames,
      newList: newProductNames,
    });
  }

  // Locations diff
  const oldLocations = request.oldValue?.interestedLocations || [];
  const newLocations = request.interestedLocations || [];

  const getLocationName = (l) => {
    if (!l) return "";
    if (typeof l === "object") {
      if (l.name) return l.name;
      if (l.cityName) return l.cityName;
    }
    const id = typeof l === "object" ? l._id : l;
    if (!id) return "";

    // Search in allLocations master list
    const foundMaster = (allLocations || []).find((item) => (item._id || item.id || item) === id);
    if (foundMaster && typeof foundMaster === "object") {
      return foundMaster.name || foundMaster.cityName || id;
    }

    // Search in newLocations
    const foundInNew = newLocations.find((item) => typeof item === "object" && (item._id || item) === id);
    if (foundInNew && foundInNew.name) {
      return foundInNew.name;
    }

    // Search in client.interestedLocations
    const foundInClient = (client?.interestedLocations || []).find((item) => typeof item === "object" && (item._id || item) === id);
    if (foundInClient && foundInClient.name) {
      return foundInClient.name;
    }

    return id;
  };

  const oldLocationNames = oldLocations.map(getLocationName);
  const newLocationNames = newLocations.map((l) => (typeof l === "object" ? l.name || l._id || l : l));

  const oldLocationIds = oldLocations.map((l) => (typeof l === "object" ? l._id : l));
  const newLocationIds = newLocations.map((l) => (typeof l === "object" ? l._id : l));

  const locationsDiff =
    oldLocationIds.length !== newLocationIds.length ||
    !oldLocationIds.every((id) => newLocationIds.some((newId) => newId === id));

  if (locationsDiff) {
    scalar.push({
      field: "interestedLocations",
      label: "Interested Locations",
      oldVal: oldLocationNames.length
        ? oldLocationNames.join(", ")
        : "None",
      newVal: newLocationNames.length
        ? newLocationNames.join(", ")
        : "None",
      isArray: true,
      oldList: oldLocationNames,
      newList: newLocationNames,
    });
  }

  return scalar;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

// ────────────────────────────────────────────────────────────────────────────
const ProfileCorrectionsLayer = () => {
  // ── States ──────────────────────────────────────────────────────────────────
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [allProducts, setAllProducts] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  // Modal states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [remarksText, setRemarksText] = useState("");
  const [requestActioning, setRequestActioning] = useState(null);

  // ── Fetch List ───────────────────────────────────────────────────────────────
  const fetchCorrections = useCallback(async () => {
    setLoading(true);
    try {
      console.log("[ProfileCorrections] Fetching list → GET /profile-corrections");
      const res = await getProfileCorrections();
      console.log("[ProfileCorrections] List response:", res);
      if (res.status) {
        const data = res.response?.data || res.response || [];
        const list = Array.isArray(data) ? data : [];
        setRequests(list);
        localStorage.setItem("profile_correction_requests", JSON.stringify(list));
        window.dispatchEvent(new Event("profile_corrections_updated"));
        console.log("[ProfileCorrections] Total records:", list.length);
      } else {
        const errorMsg = res.response?.message || "Failed to fetch corrections";
        console.error("[ProfileCorrections] Failed:", errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("[ProfileCorrections] Fetch error:", error);
      toast.error("An error occurred while fetching corrections.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCorrections();

    const fetchMasters = async () => {
      try {
        const prodRes = await getProducts();
        if (prodRes.status) {
          setAllProducts(prodRes.response?.data || prodRes.response || []);
        }
        const locRes = await getLocations();
        if (locRes.status) {
          setAllLocations(locRes.response?.data || locRes.response || []);
        }
        const catRes = await getCategories();
        if (catRes.status) {
          setAllCategories(catRes.response?.data || catRes.response || []);
        }
      } catch (err) {
        console.error("Error fetching master lists:", err);
      }
    };
    fetchMasters();
  }, [fetchCorrections]);

  // ── Derived Counts ───────────────────────────────────────────────────────────
  const counts = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "Pending").length,
    approved: requests.filter((r) => r.status === "Approved").length,
    rejected: requests.filter((r) => r.status === "Rejected").length,
  };

  // ── Filter + Paginate ────────────────────────────────────────────────────────
  const filteredRequests = requests.filter((req) => {
    const clientName = req.clientId?.keyPerson || req.keyPerson || "";
    const company = req.companyName || req.clientId?.companyName || "";
    const manager = req.managerId?.name || "";
    const reason = req.reason || "";

    const matchesSearch =
      clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      manager.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reason.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "All" || req.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalRecords = filteredRequests.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage) || 1;
  const paginatedRequests = filteredRequests.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  // ── View Details ─────────────────────────────────────────────────────────────
  const handleViewDetails = async (req) => {
    const id = req._id || req.id;
    try {
      console.log(`[ProfileCorrections] Fetching detail → GET /profile-corrections/${id}`);
      const res = await getProfileCorrectionById(id);
      console.log("[ProfileCorrections] Detail response:", res);
      if (res.status) {
        // Response shape: { data: { request: {...}, client: {...} } }
        const data = res.response?.data || res.response || {};
        const request = data.request || data || req;
        const client = data.client || null;
        console.log("[ProfileCorrections] Request:", request);
        console.log("[ProfileCorrections] Client:", client);
        setSelectedRequest({ request, client });
        setRemarksText(request.adminRemarks || "");
      } else {
        // Fallback to list row data
        const errorMsg = res.response?.message || "Failed to fetch details";
        console.warn("[ProfileCorrections] Detail fetch failed, using fallback:", errorMsg);
        setSelectedRequest({ request: req, client: null });
        setRemarksText(req.adminRemarks || "");
        toast.warning("Details partially loaded. Some information may be outdated.");
      }
    } catch (error) {
      console.error("[ProfileCorrections] Detail fetch error:", error);
      setSelectedRequest({ request: req, client: null });
      setRemarksText(req.adminRemarks || "");
      toast.warning("Could not load full details. Displaying available information.");
    }
    setShowDetailsModal(true);
  };

  // ── Approve ──────────────────────────────────────────────────────────────────
  const handleApproveClick = (req, e) => {
    e?.stopPropagation();
    setRequestActioning(req);
    setRemarksText(req.adminRemarks || "");
    setShowApproveConfirm(true);
  };

  const confirmApprove = async () => {
    if (!requestActioning) return;
    const id = requestActioning._id || requestActioning.id;
    const payload = {
      status: "Approved",
      adminRemarks: remarksText.trim() || "Verified & approved by Admin",
    };
    console.log(
      `[ProfileCorrections] Approving → PUT /profile-corrections/${id}/status`,
      payload
    );
    setActionLoading(true);
    try {
      const res = await updateProfileCorrectionStatus(id, payload);
      console.log("[ProfileCorrections] Approve response:", res);
      if (res.status) {
        toast.success("Request approved successfully!");
        setShowApproveConfirm(false);
        setShowDetailsModal(false);
        setRequestActioning(null);
        setRemarksText("");
        await fetchCorrections();
      } else {
        const errorMsg = res.response?.message || "Failed to approve request. Please try again.";
        toast.error(errorMsg);
        console.error("[ProfileCorrections] Approve failed:", res.response);
      }
    } catch (error) {
      console.error("[ProfileCorrections] Approve error:", error);
      toast.error("An error occurred while approving the request.");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Reject ───────────────────────────────────────────────────────────────────
  const handleRejectClick = (req, e) => {
    e?.stopPropagation();
    setRequestActioning(req);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!requestActioning) return;
    if (!rejectReason.trim()) {
      toast.error("Please enter a rejection reason.");
      return;
    }
    const id = requestActioning._id || requestActioning.id;
    const payload = {
      status: "Rejected",
      adminRemarks: rejectReason.trim(),
    };
    console.log(
      `[ProfileCorrections] Rejecting → PUT /profile-corrections/${id}/status`,
      payload
    );
    setActionLoading(true);
    try {
      const res = await updateProfileCorrectionStatus(id, payload);
      console.log("[ProfileCorrections] Reject response:", res);
      if (res.status) {
        toast.info("Request rejected.");
        setShowRejectModal(false);
        setShowDetailsModal(false);
        setRequestActioning(null);
        setRejectReason("");
        await fetchCorrections();
      } else {
        const errorMsg = res.response?.message || "Failed to reject request. Please try again.";
        toast.error(errorMsg);
        console.error("[ProfileCorrections] Reject failed:", res.response);
      }
    } catch (error) {
      console.error("[ProfileCorrections] Reject error:", error);
      toast.error("An error occurred while rejecting the request.");
    } finally {
      setActionLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-4 text-start">
      <div className="card h-100 p-0 radius-12 border-0 shadow-sm bg-white">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div
          className="card-header border-bottom py-16 px-24 d-flex flex-wrap align-items-center justify-content-between gap-3"
          style={{
            background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Icon icon="solar:user-speak-bold" className="text-white text-xl" />
            <h6 className="text-white mb-0 fw-bold">Correction Requests</h6>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="navbar-search position-relative" style={{ minWidth: "220px" }}>
              <input
                type="text"
                className="form-control radius-8 ps-40 bg-white border-0 py-8"
                style={{ height: "38px", fontSize: "14px" }}
                placeholder="Search by name, company..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
              />
              <Icon
                icon="solar:magnifer-linear"
                className="position-absolute text-secondary-light"
                style={{ left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "16px" }}
              />
            </div>
            {/* <button
              type="button"
              className="btn btn-sm d-flex align-items-center gap-2 radius-8 text-white border-white border fw-bold"
              style={{ fontSize: "12px", opacity: 0.85 }}
              onClick={fetchCorrections}
              disabled={loading}
            >
              <Icon icon="solar:refresh-bold" />
              Refresh
            </button> */}
          </div>
        </div>

        <div className="card-body p-24">

          {/* ── Count Cards ───────────────────────────────────────────────── */}
          <div className="row gy-3 mb-20">
            {[
              { key: "All", label: "Total Requests", count: counts.total, icon: "solar:file-text-bold-duotone", activeBorder: "#ed1c24", activeBg: "#fff5f5", iconBg: "#fee2e2", iconColor: "#ed1c24" },
              { key: "Pending", label: "Pending", count: counts.pending, icon: "solar:clock-circle-bold-duotone", activeBorder: "#f0ad4e", activeBg: "#fffbeb", iconBg: "#fef3c7", iconColor: "#f0ad4e", textClass: "text-warning" },
              { key: "Approved", label: "Approved", count: counts.approved, icon: "solar:check-circle-bold-duotone", activeBorder: "#5cb85c", activeBg: "#f0fdf4", iconBg: "#dcfce7", iconColor: "#5cb85c", textClass: "text-success" },
              { key: "Rejected", label: "Rejected", count: counts.rejected, icon: "solar:close-circle-bold-duotone", activeBorder: "#d9534f", activeBg: "#fdf2f2", iconBg: "#fee2e2", iconColor: "#d9534f", textClass: "text-danger" },
            ].map((card) => (
              <div className="col-12 col-sm-6 col-lg-3" key={card.key}>
                <div
                  className="card shadow-none cursor-pointer transition-all"
                  style={{
                    border: statusFilter === card.key ? `1px solid ${card.activeBorder}` : "1px solid #e9ecef",
                    backgroundColor: statusFilter === card.key ? card.activeBg : "#fff",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                  onClick={() => { setStatusFilter(card.key); setCurrentPage(0); }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <span className="text-secondary-light fw-bold uppercase" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                        {card.label}
                      </span>
                      <h5 className={`fw-bold mb-0 mt-4 ${card.textClass || ""}`} style={!card.textClass ? { color: "#2d3748" } : {}}>
                        {card.count}
                      </h5>
                    </div>
                    <div className="radius-6 d-flex align-items-center justify-content-center w-32-px h-32-px"
                      style={{ backgroundColor: card.iconBg, color: card.iconColor }}>
                      <Icon icon={card.icon} style={{ fontSize: "16px" }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Table ─────────────────────────────────────────────────────── */}
          {loading ? (
            <div className="row gy-4">
              {[1, 2, 3].map((n) => (
                <div className="col-12" key={n}>
                  <div className="border radius-12 p-20 bg-light" style={{ minHeight: "70px", opacity: 0.7 }}>
                    <div className="d-flex justify-content-between mb-3">
                      <div className="bg-secondary-light radius-6" style={{ width: "180px", height: "16px" }} />
                      <div className="bg-secondary-light radius-6" style={{ width: "80px", height: "16px" }} />
                    </div>
                    <div className="bg-secondary-light radius-6" style={{ width: "60%", height: "14px" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : paginatedRequests.length > 0 ? (
            <div className="table-responsive">
              <table className="table bordered-table mb-0 align-middle">
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    <th style={{ color: "#2d3748", fontWeight: "700", width: "55px" }}>S.No</th>
                    <th style={{ color: "#2d3748", fontWeight: "700" }}>Client</th>
                    <th style={{ color: "#2d3748", fontWeight: "700" }}>Manager</th>
                    <th style={{ color: "#2d3748", fontWeight: "700" }}>Changed Fields</th>
                    <th style={{ color: "#2d3748", fontWeight: "700" }}>Reason</th>
                    <th style={{ color: "#2d3748", fontWeight: "700" }}>Submitted</th>
                    <th style={{ color: "#2d3748", fontWeight: "700", width: "110px" }}>Status</th>
                    <th style={{ color: "#2d3748", fontWeight: "700", width: "100px" }} className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequests.map((req, index) => {
                    const changedFields = getChangedFields(req, allCategories);
                    const clientName = req.clientId?.keyPerson || req.keyPerson || "—";
                    const companyName = req.companyName || req.clientId?.companyName || "—";
                    const managerName = req.managerId?.name || "—";
                    const managerPhone = req.managerId?.phoneNumber || "";

                    return (
                      <tr key={req._id}>
                        {/* S.No */}
                        <td className="text-secondary-light text-sm">
                          {currentPage * rowsPerPage + index + 1}
                        </td>

                        {/* Client */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div>
                              <p className="mb-0 fw-bold text-sm d-flex align-items-center gap-2" style={{ color: "#2d3748" }}>
                                {clientName}
                                {!req.clientId && (
                                  <span className="badge bg-info-focus text-info-main fw-bold" style={{ fontSize: "9px", padding: "4px 8px" }}>
                                    New Client
                                  </span>
                                )}
                              </p>
                              <span className="text-secondary-light" style={{ fontSize: "11px" }}>
                                {companyName}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Manager */}
                        <td>
                          <p className="mb-0 fw-semibold text-sm" style={{ color: "#2d3748" }}>
                            {managerName}
                          </p>
                          {managerPhone && (
                            <span className="text-secondary-light" style={{ fontSize: "11px" }}>
                              {managerPhone}
                            </span>
                          )}
                        </td>

                        {/* Changed Fields */}
                        <td>
                          {changedFields.length === 0 ? (
                            <span className="text-secondary-light text-xs">No changes detected</span>
                          ) : (
                            <div className="d-flex flex-column gap-1">
                              {changedFields.slice(0, 2).map((ch) => (
                                <div key={ch.field} className="d-flex align-items-center gap-1 flex-wrap">
                                  <span className="badge bg-light text-dark border fw-bold" style={{ fontSize: "10px" }}>
                                    {ch.label}
                                  </span>
                                  <span className="text-danger text-decoration-line-through" style={{ fontSize: "11px" }}>
                                    {ch.oldVal}
                                  </span>
                                  <Icon icon="solar:arrow-right-linear" className="text-secondary" style={{ fontSize: "11px" }} />
                                  <span className="text-success fw-bold" style={{ fontSize: "11px" }}>
                                    {ch.newVal}
                                  </span>
                                </div>
                              ))}
                              {changedFields.length > 2 && (
                                <span className="text-secondary-light" style={{ fontSize: "11px" }}>
                                  +{changedFields.length - 2} more changes
                                </span>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Reason */}
                        <td>
                          <span
                            className="text-secondary-light text-xs text-truncate d-block"
                            style={{ maxWidth: "180px" }}
                            title={req.reason}
                          >
                            {req.reason || "—"}
                          </span>
                        </td>

                        {/* Date */}
                        <td>
                          <span className="text-sm text-secondary-light">
                            {formatDate(req.createdAt)}
                          </span>
                        </td>

                        {/* Status */}
                        <td>
                          <span className={`badge px-3 py-2 radius-8 fw-bold text-xs ${req.status === "Pending"
                            ? "bg-warning-focus text-warning-main"
                            : req.status === "Approved"
                              ? "bg-success-focus text-success-main"
                              : "bg-danger-focus text-danger-main"
                            }`}>
                            {req.status}
                          </span>
                        </td>

                        {/* ── Action ── */}
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            {/* View */}
                            <button
                              type="button"
                              title="View Details"
                              className="btn btn-sm d-flex align-items-center justify-content-center radius-8"
                              style={{ width: "30px", height: "30px", padding: 0, backgroundColor: "#eef2ff", color: "#4f46e5", border: "1px solid #e0e7ff" }}
                              onClick={() => handleViewDetails(req)}
                            >
                              <Icon icon="lucide:eye" style={{ fontSize: "14px" }} />
                            </button>

                            {/* Approve (Pending only) */}
                            {req.status === "Pending" && (
                              <button
                                type="button"
                                title="Approve"
                                className="btn btn-sm d-flex align-items-center justify-content-center radius-8"
                                style={{ width: "30px", height: "30px", padding: 0, backgroundColor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}
                                onClick={(e) => handleApproveClick(req, e)}
                              >
                                <Icon icon="solar:check-circle-bold" style={{ fontSize: "14px" }} />
                              </button>
                            )}

                            {/* Reject (Pending only) */}
                            {req.status === "Pending" && (
                              <button
                                type="button"
                                title="Reject"
                                className="btn btn-sm d-flex align-items-center justify-content-center radius-8"
                                style={{ width: "30px", height: "30px", padding: 0, backgroundColor: "#fdf2f2", color: "#d9534f", border: "1px solid #ffc9c9" }}
                                onClick={(e) => handleRejectClick(req, e)}
                              >
                                <Icon icon="solar:close-circle-bold" style={{ fontSize: "14px" }} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-48 border radius-12 bg-light">
              <div className="d-flex justify-content-center mb-3">
                <div className="bg-secondary-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                  <Icon icon="solar:user-speak-bold-duotone" className="text-secondary text-xxl" />
                </div>
              </div>
              <h5 className="fw-bold" style={{ color: "#2d3748" }}>No correction requests found</h5>
              <p className="text-secondary-light mb-0 px-24">
                No requests match the current filter.
              </p>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-20">
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(0); }}
              totalRecords={totalRecords}
            />
          </div>
        </div>
      </div>

      {/* ── Details Modal ─────────────────────────────────────────────────────── */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="xl">
        <Modal.Header
          closeButton
          className="border-0 pb-0"
          style={{ background: "linear-gradient(135deg, #0d0d0d 0%, #7b0004 50%, #ed1c24 100%)", borderRadius: "8px 7px 0 0" }}
        >
          <div className="d-flex align-items-center gap-3 py-4">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ width: "42px", height: "42px", backgroundColor: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)" }}
            >
              <Icon icon="solar:user-speak-bold-duotone" style={{ fontSize: "22px", color: "#fff" }} />
            </div>
            <div>
              <h5 className="mb-0 fw-bold text-white" style={{ fontSize: "16px", letterSpacing: "0.3px" }}>
                Profile Correction — Request Details
              </h5>
              <span className="text-white opacity-50" style={{ fontSize: "11px" }}>Review &amp; take action on the submitted correction</span>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ background: "#f4f6f9", maxHeight: "75vh", overflowY: "auto" }}>
          {selectedRequest && (() => {
            // Detail response shape: { request, client }
            const request = selectedRequest.request || selectedRequest;
            const client = selectedRequest.client || null;

            const changedFields = getDetailChangedFields(request, client, allProducts, allLocations, allCategories);
            const statusColor = request.status === "Approved" ? "#16a34a" : request.status === "Rejected" ? "#d9534f" : "#f0ad4e";
            const statusBg = request.status === "Approved" ? "#f0fdf4" : request.status === "Rejected" ? "#fdf2f2" : "#fffbeb";

            // Resolve Exhibition Type, products, and locations for New Client Details UI
            const categoryName = (() => {
              const catVal = request.categoryId;
              if (!catVal) return "—";
              const id = typeof catVal === "object" ? catVal._id || catVal.id : catVal;
              const found = (allCategories || []).find((c) => (c._id || c.id) === id);
              return found ? found.name : id;
            })();

            const productNamesList = (request.products || []).map((p) => {
              if (typeof p === "object") return p.productName || p.name || p._id;
              const found = (allProducts || []).find((item) => (item._id || item.id) === p);
              return found ? found.productName || found.name : p;
            });

            const locationNamesList = (request.interestedLocations || []).map((l) => {
              if (typeof l === "object") return l.name || l.cityName || l._id;
              const found = (allLocations || []).find((item) => (item._id || item.id) === l);
              return found ? found.name || found.cityName : l;
            });

            // Client info — prefer `client` (actual current data), fallback to request fields
            const clientName = client?.keyPerson || request.keyPerson || "—";
            const companyName = client?.companyName || request.companyName || "—";
            const clientEmail = client?.email || request.email || "—";
            const clientMobile = client?.mobile1 || request.mobile1 || "—";
            const clientWA = client?.whatsappNumber || request.whatsappNumber || "—";
            const clientStreet = client?.street || "—";
            const clientArea = client?.area || "—";
            const clientCity = client?.city || "—";
            const clientState = client?.state || "—";
            const clientPincode = client?.pincode || "—";
            const clientGroup = client?.groupId?.groupName || request.groupId?.groupName || "—";
            const clientMgr = client?.managerId?.name || request.managerId?.name || "—";

            // Manager who is handling this request
            const reqMgrName = request.managerId?.name || "—";
            const reqMgrEmail = request.managerId?.email || "";
            const reqMgrPhone = request.managerId?.phoneNumber || "";
            return (
              <>
                {/* Hero Banner */}
                <div
                  className="d-flex align-items-center gap-16 flex-wrap"
                  style={{ background: "#ffff", borderBottom: "2px solid #ed1c24", padding: "16px 24px" }}
                >
                  {/* Identity */}
                  <div className="flex-grow-1 d-flex align-items-center justify-content-between gap-16 flex-wrap">
                    <div className="d-flex align-items-center gap-12 flex-wrap">
                      {/* Name */}
                      <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: "18px", letterSpacing: "-0.2px" }}>
                        {clientName}
                        {!request.clientId && (
                          <span className="badge bg-info-focus text-info-main fw-bold" style={{ fontSize: "10px", padding: "4px 8px" }}>
                            New Client Request
                          </span>
                        )}
                      </h4>
                      
                      {/* Group/Developer Badge */}
                      {clientGroup && clientGroup !== "—" && (
                        <span className="badge px-8 py-4 radius-4 fw-bold" style={{ backgroundColor: "#ffb5b5ff", color: "#ed1c24", fontSize: "10px" }}>
                          {clientGroup}
                        </span>
                      )}

                      {/* Divider */}
                      <span className="text-secondary opacity-50">•</span>

                      {/* Company Name */}
                      <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{companyName}</span>

                      {/* Divider */}
                      <span className="text-secondary opacity-50">•</span>

                      {/* Contact Info (Inline with icons) */}
                      <div className="d-flex align-items-center gap-12 text-secondary" style={{ fontSize: "12px" }}>
                        {clientEmail !== "—" && (
                          <span className="d-flex align-items-center gap-1">
                            <Icon icon="solar:letter-linear" className="text-danger" style={{ fontSize: "14px" }} />
                            {clientEmail}
                          </span>
                        )}
                        {clientMobile !== "—" && (
                          <span className="d-flex align-items-center gap-1">
                            <Icon icon="solar:phone-linear" className="text-danger" style={{ fontSize: "14px" }} />
                            {clientMobile}
                          </span>
                        )}
                        <span className="d-flex align-items-center gap-1">
                          <Icon icon="solar:calendar-linear" className="text-danger" style={{ fontSize: "14px" }} />
                          {formatDate(request.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <span className="badge px-10 py-4 radius-6 fw-bold" style={{ backgroundColor: statusBg, color: statusColor, fontSize: "11px", border: `1px solid ${statusColor}22` }}>
                      {request.status}
                    </span>
                  </div>

                </div>

                {/* Body Grid */}
                <div className="p-24">
                  <div className="row gy-20">

                    {/* ─── LEFT COL ─── */}
                    <div className="col-12 col-lg-7">

                      {/* Client Profile Card */}
                      {/* <div className="card border-0 radius-16 shadow-sm mb-20 overflow-hidden bg-white">
                        <div className="px-20 py-14 d-flex align-items-center gap-2 border-bottom">
                          <div className="d-flex align-items-center justify-content-center radius-8" style={{ width: "30px", height: "30px", backgroundColor: "#fee2e2" }}>
                            <Icon icon="solar:user-id-bold-duotone" style={{ fontSize: "16px", color: "#ed1c24" }} />
                          </div>
                          <span className="fw-bold text-dark" style={{ fontSize: "14px", padding: '10px' }}>Current Client Profile</span>
                        </div>
                        <div className="p-20">
                          <div className="row gy-10">
                            {[
                              { icon: "solar:letter-bold-duotone", label: "Email", value: clientEmail, color: "#6366f1" },
                              { icon: "solar:phone-bold-duotone", label: "Mobile", value: clientMobile, color: "#0891b2" },
                              { icon: "solar:chat-round-bold-duotone", label: "WhatsApp", value: clientWA, color: "#16a34a" },
                              { icon: "solar:map-point-bold-duotone", label: "City / State", value: [clientCity, clientState, clientPincode].filter(v => v && v !== "\u2014").join(", ") || "\u2014", color: "#d97706" },
                              { icon: "solar:user-check-bold-duotone", label: "Manager", value: clientMgr, color: "#7c3aed" },
                            ].map((row) => (
                              <div className="col-12" key={row.label}>
                                <div className="d-flex align-items-center gap-12 p-10 radius-10" style={{ backgroundColor: "#f8f9fc" }}>
                                  <div className="d-flex align-items-center justify-content-center flex-shrink-0 radius-8"
                                    style={{ width: "32px", height: "32px", backgroundColor: row.color + "18" }}>
                                    <Icon icon={row.icon} style={{ fontSize: "16px", color: row.color }} />
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="text-secondary-light fw-semibold" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>{row.label.toUpperCase()}</div>
                                    <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>{row.value}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {client?.products?.length > 0 && (
                            <div className="mt-16 pt-16 border-top">
                              <div className="d-flex align-items-center gap-2 mb-10">
                                <Icon icon="solar:box-bold-duotone" style={{ fontSize: "15px", color: "#0891b2" }} />
                                <span className="fw-bold text-dark" style={{ fontSize: "12px" }}>Products</span>
                              </div>
                              <div className="d-flex flex-wrap gap-1">
                                {client.products.map((p) => (
                                  <span key={p._id} className="badge px-8 py-4 radius-6 fw-semibold"
                                    style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontSize: "10px" }}>
                                    {p.productName}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {client?.interestedLocations?.length > 0 && (
                            <div className="mt-12 pt-12 border-top">
                              <div className="d-flex align-items-center gap-2 mb-10">
                                <Icon icon="solar:map-bold-duotone" style={{ fontSize: "15px", color: "#d97706" }} />
                                <span className="fw-bold text-dark" style={{ fontSize: "12px" }}>Interested Locations</span>
                              </div>
                              <div className="d-flex flex-wrap gap-1">
                                {client.interestedLocations.map((l) => (
                                  <span key={l._id} className="badge px-8 py-4 radius-6 fw-semibold"
                                    style={{ backgroundColor: "#fef3c7", color: "#92400e", fontSize: "10px" }}>
                                    {l.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div> */}

                      {/* Requested Changes / New Client Details Card */}
                      {!request.clientId ? (
                        <div className="card border-0 radius-16 shadow-sm overflow-hidden bg-white">
                          <div className="px-20 py-14 d-flex align-items-center gap-2 border-bottom" style={{ padding: '10px' }}>
                            <div className="d-flex align-items-center justify-content-center radius-8" style={{ width: "30px", height: "30px", backgroundColor: "#fee2e2" }}>
                              <Icon icon="solar:user-plus-rounded-bold-duotone" style={{ fontSize: "16px", color: "#ed1c24" }} />
                            </div>
                            <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>New Client Details</span>
                          </div>
                          <div className="p-20">
                            <div className="row gy-3">
                              <div className="col-12 col-md-6">
                                <div className="p-12 radius-10 border bg-light h-100">
                                  <h6 className="fw-bold text-dark mb-10 text-xs text-secondary-light uppercase" style={{ letterSpacing: '0.5px' }}>Basic Information</h6>
                                  <div className="d-flex flex-column gap-2">
                                    <div>
                                      <span className="text-secondary-light text-xs">Company Name:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{request.companyName || "—"}</p>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Key Person:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{request.keyPerson || "—"}</p>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Segment:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{request.segment || "—"}</p>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Ratings:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{mapRatingValue(request.ratings) || "—"}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
 
                              <div className="col-12 col-md-6">
                                <div className="p-12 radius-10 border bg-light h-100">
                                  <h6 className="fw-bold text-dark mb-10 text-xs text-secondary-light uppercase" style={{ letterSpacing: '0.5px' }}>Contact Information</h6>
                                  <div className="d-flex flex-column gap-2">
                                    <div>
                                      <span className="text-secondary-light text-xs">Primary Mobile:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{request.mobile1 || "—"}</p>
                                    </div>
                                    {(request.mobile2 || request.mobile3) && (
                                      <div>
                                        <span className="text-secondary-light text-xs">Alternative Mobiles:</span>
                                        <p className="fw-bold text-sm text-dark mb-0">
                                          {[request.mobile2, request.mobile3].filter(Boolean).join(", ")}
                                        </p>
                                      </div>
                                    )}
                                    {request.email && (
                                      <div>
                                        <span className="text-secondary-light text-xs">Email:</span>
                                        <p className="fw-bold text-sm text-dark mb-0">{request.email}</p>
                                      </div>
                                    )}
                                    {request.whatsappNumber && (
                                      <div>
                                        <span className="text-secondary-light text-xs">WhatsApp Number:</span>
                                        <p className="fw-bold text-sm text-dark mb-0">{request.whatsappNumber}</p>
                                      </div>
                                    )}
                                    {request.websiteLink && (
                                      <div>
                                        <span className="text-secondary-light text-xs">Website:</span>
                                        <p className="fw-bold text-sm text-dark mb-0 text-break">{request.websiteLink}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
 
                              <div className="col-12 col-md-6">
                                <div className="p-12 radius-10 border bg-light h-100">
                                  <h6 className="fw-bold text-dark mb-10 text-xs text-secondary-light uppercase" style={{ letterSpacing: '0.5px' }}>Address Details</h6>
                                  <div className="d-flex flex-column gap-2">
                                    <div>
                                      <span className="text-secondary-light text-xs">City:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{request.city || "—"}</p>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Pincode:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{request.pincode || "—"}</p>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Street/Address:</span>
                                      <p className="fw-bold text-sm text-dark mb-0 text-break">{request.street || request.address || "—"}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
 
                              <div className="col-12 col-md-6">
                                <div className="p-12 radius-10 border bg-light h-100">
                                  <h6 className="fw-bold text-dark mb-10 text-xs text-secondary-light uppercase" style={{ letterSpacing: '0.5px' }}>Exhibition Information</h6>
                                  <div className="d-flex flex-column gap-2">
                                    <div>
                                      <span className="text-secondary-light text-xs">Exhibition Type:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{categoryName}</p>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Products:</span>
                                      <div className="d-flex flex-wrap gap-1 mt-4">
                                        {productNamesList.length > 0 ? (
                                          productNamesList.map((p, idx) => (
                                            <span key={idx} className="badge bg-info-focus text-info-main fw-bold" style={{ fontSize: '10px' }}>{p}</span>
                                          ))
                                        ) : (
                                          <span className="text-dark fw-bold text-sm">—</span>
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Interested Locations:</span>
                                      <div className="d-flex flex-wrap gap-1 mt-4">
                                        {locationNamesList.length > 0 ? (
                                          locationNamesList.map((l, idx) => (
                                            <span key={idx} className="badge bg-success-focus text-success-main fw-bold" style={{ fontSize: '10px' }}>{l}</span>
                                          ))
                                        ) : (
                                          <span className="text-dark fw-bold text-sm">—</span>
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-secondary-light text-xs">Customer Since:</span>
                                      <p className="fw-bold text-sm text-dark mb-0">{getYearOnly(request.customerSince) || "—"}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
 
                            {request.reason && (
                              <div className="mt-16 p-14 radius-12" style={{ backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
                                <div className="d-flex align-items-center gap-2 mb-6">
                                  <Icon icon="solar:chat-square-bold-duotone" style={{ fontSize: "15px", color: "#d97706" }} />
                                  <span className="fw-bold text-xs" style={{ color: "#92400e", letterSpacing: "0.5px" }}>REASON SUBMITTED</span>
                                </div>
                                <p className="mb-0 text-sm" style={{ color: "#78350f", fontStyle: "italic" }}>"{request.reason}"</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="card border-0 radius-16 shadow-sm overflow-hidden bg-white">
                          <div className="px-20 py-14 d-flex align-items-center justify-content-between border-bottom" style={{ padding: '10px' }}>
                            <div className="d-flex align-items-center gap-2">
                              <div className="d-flex align-items-center justify-content-center radius-8" style={{ width: "30px", height: "30px", backgroundColor: "#fee2e2" }}>
                                <Icon icon="solar:pen-new-square-bold-duotone" style={{ fontSize: "15px", color: "#ed1c24" }} />
                              </div>
                              <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>Requested Changes</span>
                            </div>
                            <span className="badge  radius-20 fw-bold" style={{ backgroundColor: "#fee2e2", color: "#ed1c24", fontSize: "11px" }}>
                              {changedFields.length} change{changedFields.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="p-20">
                            {changedFields.length === 0 ? (
                              <div className="text-center py-20">
                                <Icon icon="solar:check-circle-bold-duotone" style={{ fontSize: "32px", color: "#16a34a" }} />
                                <p className="text-secondary-light text-sm mb-0 mt-8">No field changes detected.</p>
                              </div>
                            ) : (
                              <div className="d-flex flex-column gap-10">
                                {changedFields.map((ch, ci) => (
                                  <div key={ch.field} className="radius-12 overflow-hidden" style={{ border: "1px solid #f0f0f0", backgroundColor: "#fafafa" }}>
                                    <div className="px-14 py-8 d-flex align-items-center gap-2" style={{ backgroundColor: "#f1f3f8", borderBottom: "1px solid #ebebeb" }}>
                                      <div className="d-flex align-items-center justify-content-center rounded-circle"
                                        style={{ width: "20px", height: "20px", backgroundColor: "#ed1c24", color: "#fff", fontSize: "10px", fontWeight: 700 }}>
                                        {ci + 1}
                                      </div>
                                      <span className="fw-bold" style={{ fontSize: "11px", color: "#374151", letterSpacing: "0.4px" }}>
                                        {ch.label.toUpperCase()}
                                      </span>
                                    </div>
                                    {ch.isArray ? (
                                      <div className="p-14">
                                        <div className="mb-10">
                                          <div className="text-xs fw-bold mb-6" style={{ color: "#b91c1c", letterSpacing: "0.5px" }}>BEFORE</div>
                                          <div className="d-flex flex-wrap gap-1">
                                            {ch.oldList.map((v, i) => (
                                              <span key={i} className="badge px-8 py-4 radius-6 fw-semibold text-decoration-line-through"
                                                style={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontSize: "10px" }}>{v}</span>
                                            ))}
                                          </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 my-8">
                                          <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
                                          <div className="d-flex align-items-center justify-content-center rounded-circle"
                                            style={{ width: "24px", height: "24px", backgroundColor: "#ed1c24" }}>
                                            <Icon icon="solar:arrow-down-bold" style={{ fontSize: "12px", color: "#fff" }} />
                                          </div>
                                          <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
                                        </div>
                                        <div>
                                          <div className="text-xs fw-bold mb-6" style={{ color: "#15803d", letterSpacing: "0.5px" }}>AFTER</div>
                                          <div className="d-flex flex-wrap gap-1">
                                            {ch.newList.map((v, i) => (
                                              <span key={i} className="badge px-8 py-4 radius-6 fw-bold"
                                                style={{ backgroundColor: "#dcfce7", color: "#15803d", fontSize: "10px" }}>{v}</span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="p-14">
                                        <div className="d-flex align-items-stretch gap-10">
                                          <div style={{ flex: 1 }}>
                                            <div className="text-xs fw-bold mb-6" style={{ color: "#b91c1c", letterSpacing: "0.5px" }}>BEFORE</div>
                                            <div className="p-10 radius-8 fw-bold text-sm text-decoration-line-through text-break"
                                              style={{ backgroundColor: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca" }}>
                                              {ch.oldVal}
                                            </div>
                                          </div>
                                          <div className="d-flex align-items-center flex-shrink-0">
                                            <div className="d-flex align-items-center justify-content-center rounded-circle"
                                              style={{ width: "28px", height: "28px", backgroundColor: "#ed1c24", boxShadow: "0 2px 8px rgba(237,28,36,0.35)" }}>
                                              <Icon icon="solar:arrow-right-bold" style={{ fontSize: "13px", color: "#fff" }} />
                                            </div>
                                          </div>
                                          <div style={{ flex: 1 }}>
                                            <div className="text-xs fw-bold mb-6" style={{ color: "#15803d", letterSpacing: "0.5px" }}>AFTER</div>
                                            <div className="p-10 radius-8 fw-bold text-sm text-break"
                                              style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" }}>
                                              {ch.newVal}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {request.reason && (
                              <div className="mt-16 p-14 radius-12" style={{ backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
                                <div className="d-flex align-items-center gap-2 mb-6">
                                  <Icon icon="solar:chat-square-bold-duotone" style={{ fontSize: "15px", color: "#d97706" }} />
                                  <span className="fw-bold text-xs" style={{ color: "#92400e", letterSpacing: "0.5px" }}>REASON SUBMITTED</span>
                                </div>
                                <p className="mb-0 text-sm" style={{ color: "#78350f", fontStyle: "italic" }}>"{request.reason}"</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    </div> {/* end left col */}

                    {/* ── Right: Manager + Admin Ops ── */}
                    <div className="col-12 col-lg-5">

                      {/* Manager Ribbon */}
                      <div className="card border-0 radius-16 shadow-sm mb-20 overflow-hidden">
                        <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)" }} className="p-20">
                          <div className="d-flex align-items-center gap-2 mb-14">
                            <Icon icon="solar:user-rounded-bold-duotone" style={{ fontSize: "15px", color: "rgba(255,255,255,0.7)" }} />
                            <span className="fw-bold text-white opacity-75" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>REQUEST MANAGER</span>
                          </div>
                          <div className="d-flex align-items-center gap-14" style={{marginLeft:"5px"}}>
                            <div>
                              <p className="mb-2 fw-bold text-white" style={{ fontSize: "15px" }}>{reqMgrName}</p>
                              {reqMgrEmail && (
                                <div className="d-flex align-items-center gap-2 text-white opacity-70 mb-4" style={{ fontSize: "11px" }}>
                                  <Icon icon="solar:letter-linear" style={{ fontSize: "13px" }} /> {reqMgrEmail}
                                </div>
                              )}
                              {reqMgrPhone && (
                                <div className="d-flex align-items-center gap-2 text-white opacity-70" style={{ fontSize: "11px" }}>
                                  <Icon icon="solar:phone-linear" style={{ fontSize: "13px" }} /> {reqMgrPhone}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-14 pt-14 d-flex align-items-center gap-2"
                            style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
                            <Icon icon="solar:calendar-bold-duotone" style={{ fontSize: "14px", color: "rgba(255,255,255,0.5)" }} />
                            <span className="text-white opacity-50" style={{ fontSize: "11px" }}>
                              Submitted on <strong className="opacity-100">{formatDate(request.createdAt)}</strong>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Admin Operations */}
                      <div className="card border-0 radius-16 shadow-sm overflow-hidden">
                        <div className="px-20 py-14 d-flex align-items-center gap-2 border-bottom bg-white">
                          <div className="d-flex align-items-center justify-content-center radius-8"
                            style={{ width: "30px", height: "30px", backgroundColor: "#fee2e2" }}>
                            <Icon icon="solar:shield-check-bold-duotone" style={{ fontSize: "16px", color: "#ed1c24" }} />
                          </div>
                          <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>Admin Operations</span>
                        </div>
                        <div className="p-20 bg-white">
                          {request.status === "Pending" ? (
                            <div>
                              <label className="form-label fw-bold text-secondary-light d-flex align-items-center gap-2 mb-8" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                                <Icon icon="solar:pen-2-bold-duotone" style={{ fontSize: "14px", color: "#6b7280" }} />
                                ADMIN REMARKS <span className="text-secondary-light fw-normal ms-1">(Optional)</span>
                              </label>
                              <textarea
                                className="form-control radius-10 text-sm mb-16"
                                rows="3"
                                placeholder="e.g. Verified with registration document. Looks good."
                                value={remarksText}
                                onChange={(e) => setRemarksText(e.target.value)}
                                style={{ border: "1.5px solid #e5e7eb", resize: "none", fontSize: "13px" }}
                              />
                              <button
                                type="button"
                                className="btn w-100 d-flex align-items-center justify-content-center gap-2 radius-10 py-12 fw-bold mb-10"
                                onClick={() => handleApproveClick(request)}
                                disabled={actionLoading}
                                style={{ background: "linear-gradient(135deg, #16a34a, #15803d)", color: "#fff", border: "none", fontSize: "13px", boxShadow: "0 4px 14px rgba(22,163,74,0.35)" }}
                              >
                                {actionLoading ? <span className="spinner-border spinner-border-sm" /> : <Icon icon="solar:check-circle-bold-duotone" style={{ fontSize: "18px" }} />}
                                Approve Request
                              </button>
                              <button
                                type="button"
                                className="btn w-100 d-flex align-items-center justify-content-center gap-2 radius-10 py-12 fw-bold"
                                onClick={() => handleRejectClick(request)}
                                disabled={actionLoading}
                                style={{ background: "linear-gradient(135deg, #ed1c24, #bb0007)", color: "#fff", border: "none", fontSize: "13px", boxShadow: "0 4px 14px rgba(237,28,36,0.3)" }}
                              >
                                <Icon icon="solar:close-circle-bold-duotone" style={{ fontSize: "18px" }} />
                                Reject Request
                              </button>
                            </div>
                          ) : (
                            <div className="radius-12 p-16"
                              style={{ backgroundColor: request.status === "Approved" ? "#f0fdf4" : "#fdf2f2", border: `1.5px solid ${request.status === "Approved" ? "#bbf7d0" : "#fecaca"}` }}>
                              <div className="d-flex align-items-center gap-2 mb-10">
                                <div className="d-flex align-items-center justify-content-center rounded-circle"
                                  style={{ width: "36px", height: "36px", backgroundColor: request.status === "Approved" ? "#dcfce7" : "#fee2e2" }}>
                                  <Icon
                                    icon={request.status === "Approved" ? "solar:check-circle-bold-duotone" : "solar:close-circle-bold-duotone"}
                                    style={{ fontSize: "20px", color: request.status === "Approved" ? "#16a34a" : "#d9534f" }}
                                  />
                                </div>
                                <div>
                                  <div className="fw-bold" style={{ fontSize: "13px", color: request.status === "Approved" ? "#15803d" : "#b91c1c" }}>
                                    Request {request.status}
                                  </div>
                                  <div className="text-secondary-light" style={{ fontSize: "10px" }}>No further action required</div>
                                </div>
                              </div>
                              {request.adminRemarks && (
                                <div className="p-10 radius-8"
                                  style={{ backgroundColor: request.status === "Approved" ? "#dcfce7" : "#fee2e2", fontSize: "12px", color: request.status === "Approved" ? "#14532d" : "#7f1d1d" }}>
                                  <strong>Remarks:</strong> {request.adminRemarks}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="border-top d-flex justify-content-end px-24 py-14" style={{ backgroundColor: "#f4f6f9" }}>
          <Button variant="outline-secondary" className="radius-10 px-24 fw-bold" style={{ fontSize: "13px" }} onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Approve Confirm Modal ─────────────────────────────────────────────── */}
      <Modal show={showApproveConfirm} onHide={() => setShowApproveConfirm(false)} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-success-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:check-circle-bold-duotone" className="text-success text-xxl" />
            </div>
          </div>
          <h5 className="mb-2 fw-bold">Approve this request?</h5>
          <p className="text-secondary-light mb-4 text-sm">
            {requestActioning && !requestActioning.clientId
              ? "A new client profile will be created with the details provided."
              : "Client profile will be updated with the requested changes."}
          </p>
          <div className="mb-3 text-start">
            <label className="form-label text-xs fw-bold text-secondary-light uppercase">
              Admin Remarks (Optional)
            </label>
            <textarea
              className="form-control radius-8 text-sm"
              rows="2"
              placeholder="e.g. Verified with corporate registration document. Looks good."
              value={remarksText}
              onChange={(e) => setRemarksText(e.target.value)}
            />
          </div>
          <div className="d-flex justify-content-center gap-3">
            <Button variant="outline-secondary" className="px-24 radius-8" onClick={() => setShowApproveConfirm(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0 d-flex align-items-center gap-2"
              onClick={confirmApprove}
              disabled={actionLoading}
              style={{ backgroundColor: "#5cb85c", color: "white", fontWeight: "700" }}
            >
              {actionLoading ? <span className="spinner-border spinner-border-sm" /> : <Icon icon="solar:check-circle-bold" />}
              {requestActioning && !requestActioning.clientId ? "Approve & Create" : "Approve & Update"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ── Reject Modal ─────────────────────────────────────────────────────── */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: "16px" }}>Reject Correction Request</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24 text-start">
          <label className="form-label fw-bold text-secondary-light text-xs uppercase">
            Rejection Reason <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control radius-8 text-sm"
            rows="4"
            placeholder="Please enter a professional explanation for why this correction was declined..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" className="radius-8 px-24" onClick={() => setShowRejectModal(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            className="radius-8 px-24 border-0 d-flex align-items-center gap-2"
            onClick={confirmReject}
            disabled={actionLoading}
            style={{ backgroundColor: "#d9534f", color: "white", fontWeight: "700" }}
          >
            {actionLoading ? <span className="spinner-border spinner-border-sm" /> : <Icon icon="solar:close-circle-bold" />}
            Submit Reject
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProfileCorrectionsLayer;